import { createReducer } from '@reduxjs/toolkit';
import { getHasMoreToLoad } from 'reactApp/modules/userQuotaCleaner/helpers/getHasMoreToLoad';
import { mergeItemsList } from 'reactApp/modules/userQuotaCleaner/helpers/mergeItemsList';
import { State } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.types';

import { transformGroup, transformList } from './helpers/transformList';
import {
    deleteUserQuotaCleanerItemsFromStore,
    loadUserQuotaCleanerListError,
    loadUserQuotaCleanerListSuccess,
    loadUserQuotaCleanerStart,
    loadUserQuotaGroupError,
    loadUserQuotaGroupSuccess,
    prepareUserQuotaCleanerParams,
    resetUserQuotaCurrentGroup,
    setCurrentGroup,
    setIsItemsDeleteError,
    setIsItemsDeleteProcess,
    setMessagesRemove,
    setNeedsGroupReload,
} from './userQuotaCleaner.actions';

const initialState: State = {
    groups: [],
    groupsParams: null,
    currentGroup: null,
    isGroupsLoading: false,
    isGroupsLoaded: false,
    isGroupsLoadingError: false,
    needGroupsReload: false,
    isGroupLoadingError: false,
    isMessagesRemoveNeeded: false,
    isItemsDeleteProcess: false,
    isItemsDeleteError: false,
};

export const userQuotaCleanerReducer = createReducer(initialState, {
    [loadUserQuotaCleanerListSuccess.type]: (state, action: ReturnType<typeof loadUserQuotaCleanerListSuccess>) => {
        return {
            ...state,
            groups: transformList(action.payload),
            isGroupsLoading: false,
            isGroupsLoaded: true,
        };
    },
    [prepareUserQuotaCleanerParams.type]: (state, action) => {
        return {
            ...state,
            groupsParams: action.payload,
        };
    },
    [loadUserQuotaCleanerStart.type]: (state) => {
        return {
            ...state,
            isGroupsLoading: true,
            isGroupsLoaded: false,
            isGroupsLoadingError: false,
            needGroupsReload: false,
        };
    },
    [loadUserQuotaCleanerListError.type]: (state) => {
        return {
            ...state,
            groups: [],
            isGroupsLoading: false,
            isGroupsLoadingError: true,
            isGroupsLoaded: true,
        };
    },
    [loadUserQuotaGroupSuccess.type]: (state, action: ReturnType<typeof loadUserQuotaGroupSuccess>) => {
        const { currentGroup } = state;
        const {
            payload: { data, offset, limit, maxOffset, isGroupReset },
        } = action;

        const oldGroup = isGroupReset ? null : currentGroup;
        const newGroup = transformGroup(data);
        const { list, childs, totalCount } = newGroup;
        const newList = { ...(oldGroup?.list ?? {}), ...list };

        // сливаем текущий список элементов с новым
        const mergedChilds = mergeItemsList(oldGroup?.childs ?? [], childs, { offset, limit });
        const mergedList = mergedChilds.reduce(
            (acc, id) => ({
                ...acc,
                [id]: newList[id],
            }),
            {}
        );

        return {
            ...state,
            currentGroup: {
                ...newGroup,
                list: mergedList,
                childs: mergedChilds,
                hasMoreToLoad: getHasMoreToLoad({ currentCount: mergedChilds.length, totalCount, maxOffset, limit }),
            },
            loadUserQuotaGroupError: false,
        };
    },
    [deleteUserQuotaCleanerItemsFromStore.type]: (state, action: ReturnType<typeof deleteUserQuotaCleanerItemsFromStore>) => {
        const {
            payload: { deleteIds },
        } = action;
        const { currentGroup } = state;
        if (!currentGroup) {
            return state;
        }

        const { childs, list } = currentGroup;

        // собираем id элементов по deleteId
        const ids = currentGroup.childs.reduce((acc: string[], id) => {
            if (deleteIds.includes(list[id].deleteId)) {
                acc.push(id);
            }

            return acc;
        }, []);

        // удаляем из модели удаленные ранее элементы
        const newChilds = childs.filter((item) => !ids.includes(item));
        const newList = newChilds.reduce((acc, child) => ({ ...acc, [child]: list[child] }), {});

        return {
            ...state,
            currentGroup: {
                ...currentGroup,
                childs: newChilds,
                list: newList,
            },
        };
    },
    [resetUserQuotaCurrentGroup.type]: (state) => {
        return {
            ...state,
            currentGroup: null,
        };
    },
    [setNeedsGroupReload.type]: (state, action: ReturnType<typeof setNeedsGroupReload>) => {
        return {
            ...state,
            needGroupsReload: action.payload.value,
        };
    },
    [loadUserQuotaGroupError.type]: (state) => {
        return {
            ...state,
            isGroupLoadingError: true,
        };
    },
    [setMessagesRemove.type]: (state, action: ReturnType<typeof setMessagesRemove>) => {
        return {
            ...state,
            isMessagesRemoveNeeded: action.payload.value,
        };
    },
    [setCurrentGroup.type]: (state, action: ReturnType<typeof setCurrentGroup>) => {
        return {
            ...state,
            currentGroup: action.payload.value,
        };
    },
    [setIsItemsDeleteProcess.type]: (state, action: ReturnType<typeof setIsItemsDeleteProcess>) => {
        return {
            ...state,
            isItemsDeleteProcess: action.payload.value,
        };
    },
    [setIsItemsDeleteError.type]: (state, action: ReturnType<typeof setIsItemsDeleteError>) => {
        return {
            ...state,
            isItemsDeleteError: action.payload.value,
        };
    },
});
